/**
 * Browser-direct ECFS client.
 *
 * Fetches https://ecfsapi.fcc.gov/filings/:id from the user's browser. We do
 * this client-side (rather than via a Supabase edge function or other
 * datacenter host) because Akamai's Bot Manager on ecfsapi.fcc.gov blocks
 * datacenter egress (Supabase edge, Cloudflare Workers, etc.) but allows
 * residential-IP browsers with real TLS fingerprints.
 *
 * ecfsapi.fcc.gov permits CORS for cross-origin browser fetches, so no proxy
 * is required. However, Akamai Bot Manager will 403 requests that either
 *   1. carry non-default headers (e.g. `accept: application/json` instead of
 *      the browser default `accept: * / *`), or
 *   2. arrive in rapid bursts from a single origin.
 *
 * This module therefore:
 *   - omits custom headers so the browser sends its default request shape;
 *   - caps concurrent in-flight requests to `ecfsapi.fcc.gov` via a module-
 *     level semaphore (shared across every caller of getFilingDetails);
 *   - retries 403 / 429 / 5xx with exponential backoff + jitter, bounded by
 *     a single AbortController timeout that spans all attempts.
 */

import * as Sentry from '@sentry/react';

const ECFS_BASE = 'https://ecfsapi.fcc.gov/filings';
const OVERALL_TIMEOUT_MS = 15_000;
const MAX_CONCURRENT = 3;
const MAX_ATTEMPTS = 3;

const memCache = new Map<string, unknown>();

let inFlight = 0;
const waitQueue: Array<() => void> = [];

function acquire(): Promise<void> {
  return new Promise((resolve) => {
    const run = () => {
      inFlight++;
      resolve();
    };
    if (inFlight < MAX_CONCURRENT) {
      run();
    } else {
      waitQueue.push(run);
    }
  });
}

function release(): void {
  inFlight--;
  const next = waitQueue.shift();
  if (next) next();
}

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    const timer = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      signal.removeEventListener('abort', onAbort);
      reject(new DOMException('Aborted', 'AbortError'));
    };
    signal.addEventListener('abort', onAbort);
  });
}

export async function getFilingDetails(submissionId: string): Promise<any | null> {
  const id = submissionId.trim();
  if (!id) return null;
  if (memCache.has(id)) return memCache.get(id);

  await acquire();

  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), OVERALL_TIMEOUT_MS);

  try {
    let lastStatus: number | null = null;

    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
      let res: Response;
      try {
        res = await fetch(`${ECFS_BASE}/${encodeURIComponent(id)}`, {
          signal: ctrl.signal,
        });
      } catch (err) {
        const aborted = (err as { name?: string } | null)?.name === 'AbortError';
        Sentry.addBreadcrumb({
          category: 'ecfs-direct',
          level: 'warning',
          message: aborted ? 'ecfsapi client timeout' : 'ecfsapi fetch error',
          data: { submissionId: id, attempt },
        });
        return null;
      }

      if (res.ok) {
        const data = await res.json();
        memCache.set(id, data);
        return data;
      }

      lastStatus = res.status;
      const retriable = res.status === 403 || res.status === 429 || res.status >= 500;
      if (!retriable || attempt === MAX_ATTEMPTS) {
        Sentry.addBreadcrumb({
          category: 'ecfs-direct',
          level: 'warning',
          message: 'ecfsapi non-2xx',
          data: { submissionId: id, status: res.status, attempt },
        });
        return null;
      }

      const backoff = 500 * 2 ** (attempt - 1) + Math.floor(Math.random() * 500);
      try {
        await sleep(backoff, ctrl.signal);
      } catch {
        Sentry.addBreadcrumb({
          category: 'ecfs-direct',
          level: 'warning',
          message: 'ecfsapi timeout during backoff',
          data: { submissionId: id, attempt, lastStatus },
        });
        return null;
      }
    }

    return null;
  } finally {
    clearTimeout(timer);
    release();
  }
}
